import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import {
  Award, Lock, Sparkles, Share2,
  Layers, BookOpen, Trophy, Crown, Flame, Target, Zap, TrendingUp,
  Moon, Rocket, Heart, Diamond,
} from "lucide-react";
import ShareCardDialog from "@/components/ShareCard";
import GemIcon from "@/components/GemIcon";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { useTutorial } from "@/lib/tutorial";
import { Link } from "react-router-dom";

const ICONS = {
  Sparkles, Layers, BookOpen, Trophy, Crown, Flame, Target, Zap,
  TrendingUp, Moon, Rocket, Heart, Diamond, Award, Share2,
};

const RARITY = {
  common:    { label: "Common",    accent: "#A1A1AA", glow: "rgba(161,161,170,0.45)" },
  rare:      { label: "Rare",      accent: "#5AC8FA", glow: "rgba(90,200,250,0.65)" },
  epic:      { label: "Epic",      accent: "#B8A4FF", glow: "rgba(184,164,255,0.75)" },
  legendary: { label: "Legendary", accent: "#7C5CFF", glow: "rgba(124,92,255,0.90)" },
  mythic:    { label: "Mythic",    accent: "#FFD86B", glow: "rgba(255,216,107,1)" },
};

const GROUP_LABEL = {
  volume:     "Volume",
  streak:     "Streaks",
  mastery:    "Mastery",
  quality:    "Quality",
  discipline: "Discipline",
  breadth:    "Breadth",
  level:      "Level",
  secret:     "Secret",
};

const GROUP_ORDER = ["streak", "volume", "mastery", "quality", "discipline", "breadth", "level", "secret"];

export default function Achievements() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [data, setData] = useState(null);
  const [share, setShare] = useState(null);

  const { startIfNew } = useTutorial();
  useEffect(() => {
    const t = setTimeout(() => startIfNew("awards"), 900);
    return () => clearTimeout(t);
  }, []);
  const [filter, setFilter] = useState("all");

  useEffect(() => { api.get("/achievements").then((r) => setData(r.data)); }, []);

  const grouped = useMemo(() => {
    if (!data) return {};
    const groups = {};
    const seen = new Set();
    for (const a of data.earned) {
      const g = a.group || "misc";
      (groups[g] = groups[g] || { earned: [], locked: [] }).earned.push(a);
      seen.add(a.achievement_key);
    }
    for (const a of data.locked) {
      if (seen.has(a.key)) continue;
      const g = a.group || "misc";
      (groups[g] = groups[g] || { earned: [], locked: [] }).locked.push(a);
    }
    return groups;
  }, [data]);

  if (!data) return <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-14"><div className="h-12 w-1/2 shimmer rounded mb-6" /></div>;

  const total = data.earned.length + data.locked.length;
  const earnedCount = data.earned.length;

  return (
    <div className="max-w-[1180px] mx-auto px-5 md:px-8 py-10 md:py-14">
      <motion.header
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8"
      >
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="eyebrow">Awards</div>
            <h1 className="display-2 mt-2">Your <span className="grad-text-3">milestones</span></h1>
            <p className="text-[15px] text-[#B0AEBC] mt-3">
              {earnedCount} of {total} unlocked · earn gems by completing them
            </p>
          </div>
          <Link to="/app/gems" data-testid="gem-shop-link" className="group">
            <motion.div
              whileHover={{ scale: 1.04 }}
              className="surface px-5 py-3.5 inline-flex items-center gap-3 cursor-pointer"
              style={{ boxShadow: "0 0 24px -8px rgba(124,92,255,0.55)" }}
              data-testid="gem-balance"
            >
              <GemIcon size={18} />
              <span className="text-[20px] font-bold tabular-nums text-white">{data.gems}</span>
              <span className="text-[12px] text-[#8E8B99] uppercase tracking-wider group-hover:text-[#B8A4FF] transition-colors">Spend gems →</span>
            </motion.div>
          </Link>
        </div>

        {/* Rarity filter */}
        <div className="flex items-center gap-1 p-1 rounded-full bg-white/[0.04] border border-white/[0.06] inline-flex mt-7">
          {["all", "common", "rare", "epic", "legendary", "mythic"].map((r) => {
            const active = filter === r;
            const fg = theme === "light" ? "#1A1A22" : "#F4F4F5";
            const c = r === "all" ? fg : RARITY[r].accent;
            return (
              <button
                key={r}
                type="button"
                onClick={() => setFilter(r)}
                data-testid={`filter-${r}`}
                className="relative px-3.5 py-1 text-[12px] font-semibold rounded-full capitalize"
                style={{ color: active ? fg : (theme === "light" ? "#6B6B7B" : "#A1A1AA") }}
              >
                {active && (
                  <motion.span
                    layoutId="rarity-filter-pill"
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: `linear-gradient(135deg, ${c}30, ${c}15)`,
                      border: `1px solid ${c}55`,
                      boxShadow: `0 0 18px -4px ${c}`,
                    }}
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
                <span className="relative">{r}</span>
              </button>
            );
          })}
        </div>
      </motion.header>

      {GROUP_ORDER.map((g) => {
        const bucket = grouped[g];
        if (!bucket) return null;
        const cards = [
          ...bucket.earned.map((a) => ({ ...a, earned: true })),
          ...bucket.locked.map((a) => ({ ...a, earned: false })),
        ].filter((a) => filter === "all" || a.rarity === filter);

        if (cards.length === 0) return null;

        return (
          <section key={g} className="mb-10" data-testid={`group-${g}`}>
            <h2 className="text-[12px] font-bold text-[#8E8B99] uppercase tracking-[0.18em] mb-4 flex items-center gap-2">
              {GROUP_LABEL[g] || g}
              <span className="text-[#5AC8FA] tabular-nums normal-case tracking-normal">
                {bucket.earned.length}/{bucket.earned.length + bucket.locked.length}
              </span>
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {cards.map((a, i) => (
                <AchievementCard
                  key={a.id || a.key}
                  a={a}
                  i={i}
                  onShare={() => setShare({ type: "award", data: { value: a.name, sub: a.description } })}
                />
              ))}
            </div>
          </section>
        );
      })}

      <ShareCardDialog
        open={!!share}
        onClose={() => setShare(null)}
        type={share?.type}
        data={share?.data || {}}
        user={user}
      />
    </div>
  );
}

function AchievementCard({ a, i, onShare }) {
  const { theme } = useTheme();
  const r = RARITY[a.rarity] || RARITY.common;
  const Icon = ICONS[a.icon] || Award;
  const earned = a.earned;
  const hidden = !earned && a.hidden;
  const titleColor = earned ? (theme === "light" ? "#1A1A22" : "#F4F4F5") : (theme === "light" ? "#6B6B7B" : "#A1A1AA");
  const descColor = earned ? (theme === "light" ? "#565666" : "#A1A1AA") : (theme === "light" ? "#9A9AAB" : "#71717A");
  const prog = a.progress;
  const pct = prog ? Math.min(100, Math.round((prog.current / prog.target) * 100)) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: i * 0.04, type: "spring", stiffness: 220, damping: 22 }}
      whileHover={{ y: -4 }}
      className="surface p-5 relative overflow-hidden"
      style={earned ? { borderColor: `${r.accent}55`, boxShadow: `0 0 0 1px ${r.accent}33, 0 0 28px -8px ${r.glow}` } : {}}
      data-testid={`${earned ? "earned" : "locked"}-${a.key || a.achievement_key}`}
    >
      {earned && (
        <div
          className="absolute -top-12 -right-12 w-32 h-32 rounded-full pointer-events-none"
          style={{ background: `radial-gradient(circle, ${r.glow}, transparent 65%)`, filter: "blur(20px)" }}
        />
      )}
      <div className="relative flex items-start gap-3.5">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
          style={{
            background: earned
              ? `linear-gradient(135deg, ${r.accent}40, ${r.accent}15)`
              : "rgba(255,255,255,0.05)",
            border: earned ? `1px solid ${r.accent}66` : "1px solid rgba(255,255,255,0.08)",
            boxShadow: earned ? `0 0 18px -4px ${r.glow}` : "none",
          }}
        >
          {hidden ? (
            <Lock size={18} className="text-[#8E8B99]" strokeWidth={1.8} />
          ) : (
            <Icon size={20} strokeWidth={1.8} style={{ color: earned ? r.accent : "#71717A" }} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-[15px] font-semibold truncate" style={{ color: titleColor }}>
              {hidden ? "Hidden" : a.name}
            </h3>
            <span
              className="text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
              style={{ color: r.accent, background: `${r.accent}14`, border: `1px solid ${r.accent}33` }}
            >
              {r.label}
            </span>
          </div>
          <p className="text-[12.5px] leading-snug" style={{ color: descColor }}>
            {hidden ? "Complete a special action to unlock" : a.description}
          </p>
          {prog && !earned && (
            <div className="mt-2.5">
              <div className="h-1.5 progress-track">
                <motion.div
                  className="h-full"
                  style={{ background: `linear-gradient(90deg, ${r.accent}, ${r.accent}aa)`, borderRadius: 999 }}
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                />
              </div>
              <div className="text-[10.5px] text-[#8E8B99] mt-1 tabular-nums">
                {prog.current.toLocaleString()} / {prog.target.toLocaleString()}
              </div>
            </div>
          )}
          <div className="mt-3 flex items-center justify-between">
            <div className="inline-flex items-center gap-1 text-[11px] font-bold" style={{ color: earned ? r.accent : "#71717A" }}>
              <GemIcon size={11} glow={false} /> +{a.gems || a.gems_awarded}
            </div>
            {earned && (
              <button
                onClick={onShare}
                className="opacity-70 hover:opacity-100 w-7 h-7 rounded-full bg-white/8 hover:bg-white/15 flex items-center justify-center transition-all"
                data-testid={`share-${a.achievement_key}`}
                aria-label="Share"
              >
                <Share2 size={11} className="text-white" />
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
